import { useState, useEffect } from 'react';
import { OnboardingStepProps, LevelRange, LEVEL_RANGES, RoleDefinition } from './types';

const LOCATION_OPTIONS = [
  'Remote',
  'San Francisco',
  'New York',
  'London',
  'Berlin',
  'Toronto',
  'Singapore',
  'Other',
];

export function StepRole({ state, onUpdate, onNext, onBack }: OnboardingStepProps) {
  const [title, setTitle] = useState(state.role?.title ?? '');
  const [level, setLevel] = useState<LevelRange>(state.role?.level ?? 'mid');
  const [locations, setLocations] = useState<string[]>(state.role?.locations ?? []);
  const [numberOfHires, setNumberOfHires] = useState(state.role?.numberOfHires ?? 1);

  const isValid = title.trim().length > 0;

  useEffect(() => {
    if (title || level || locations.length || numberOfHires !== 1) {
      const role: RoleDefinition = {
        title: title.trim(),
        level,
        locations,
        numberOfHires,
      };
      onUpdate({ role });
    }
  }, [title, level, locations, numberOfHires]);

  const toggleLocation = (loc: string) => {
    setLocations((prev) =>
      prev.includes(loc) ? prev.filter((l) => l !== loc) : [...prev, loc]
    );
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-surface-900 mb-4">
          What role are you hiring for?
        </h1>
        <p className="text-lg text-surface-500 max-w-xl mx-auto">
          This is about deciding, not posting. We'll use this to set your evaluation bar.
        </p>
      </div>

      <div className="max-w-xl mx-auto space-y-8">
        {/* Role title */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-2">
            Role title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Senior Software Engineer"
            className="w-full px-4 py-3 border-2 border-surface-200 rounded-xl focus:border-brand-500 focus:ring-0 focus:outline-none transition-colors text-surface-900 placeholder:text-surface-400"
          />
        </div>

        {/* Level range */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-3">
            Level range
          </label>
          <div className="flex flex-wrap gap-2">
            {LEVEL_RANGES.map((l) => (
              <button
                key={l.id}
                onClick={() => setLevel(l.id)}
                className={`
                  px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200
                  ${level === l.id
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
                  }
                `}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Locations */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-3">
            Location(s) <span className="text-surface-400 font-normal">— optional</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {LOCATION_OPTIONS.map((loc) => {
              const isSelected = locations.includes(loc);
              return (
                <button
                  key={loc}
                  onClick={() => toggleLocation(loc)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border
                    ${isSelected
                      ? 'bg-brand-50 border-brand-300 text-brand-700'
                      : 'bg-white border-surface-200 text-surface-600 hover:border-surface-300'
                    }
                  `}
                >
                  {isSelected && (
                    <svg className="w-4 h-4 inline-block mr-1.5 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                  {loc}
                </button>
              );
            })}
          </div>
        </div>

        {/* Number of hires */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-2">
            Number of hires
          </label>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setNumberOfHires(Math.max(1, numberOfHires - 1))}
              className="w-10 h-10 rounded-lg border-2 border-surface-200 flex items-center justify-center text-surface-500 hover:border-surface-300 hover:text-surface-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
              </svg>
            </button>
            <span className="text-2xl font-semibold text-surface-900 w-12 text-center">
              {numberOfHires}
            </span>
            <button
              onClick={() => setNumberOfHires(numberOfHires + 1)}
              className="w-10 h-10 rounded-lg border-2 border-surface-200 flex items-center justify-center text-surface-500 hover:border-surface-300 hover:text-surface-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-12 flex justify-center gap-4">
        <button
          onClick={onBack}
          className="btn btn-secondary text-base px-8 py-3.5"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className={`
            btn text-base px-10 py-3.5 transition-all duration-200
            ${isValid
              ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-500/25'
              : 'bg-surface-200 text-surface-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}

